/**
 * The only HTTP layer of the SPA (native Fetch API).
 *
 * - Access tokens live in memory only (never localStorage) and are sent as Bearer tokens.
 * - The refresh token is an httpOnly, SameSite=Strict cookie scoped to /api/v1/auth; the client
 *   calls /auth/refresh with the CSRF header when the access token is about to expire or a request
 *   fails with an expired token. Concurrent refreshes are coalesced into one request.
 * - Every request carries an X-Request-Id so UI errors can be correlated with server logs/audit.
 * - Errors are normalized into ApiError using the server's error envelope.
 */

export interface ApiErrorDetail {
  path?: string;
  message: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string | null,
    public readonly details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation messages keyed by field path. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    // Some errors carry an object of details (e.g. { onBoardCount }) rather than field errors.
    if (!Array.isArray(this.details)) return out;
    for (const d of this.details) if (d.path && !out[d.path]) out[d.path] = d.message;
    return out;
  }
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface Paged<T> {
  data: T[];
  meta: PageMeta;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the Authorization header and token refresh (public endpoints). */
  anonymous?: boolean;
  headers?: Record<string, string>;
  /** Raw (non-JSON) request body, e.g. a File for uploads. Mutually exclusive with `body`. */
  rawBody?: Blob;
  /** 'blob' returns the response body as a Blob (downloads, photos) instead of parsing JSON. */
  responseType?: 'json' | 'blob';
}

interface TokenResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
}

const CSRF_HEADER = 'x-intellschools-csrf';
const EXPIRY_SKEW_MS = 30_000;
const AUTH_RETRY_CODES = new Set(['TOKEN_EXPIRED', 'INVALID_TOKEN', 'UNAUTHENTICATED']);

function newRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export class ApiClient {
  private accessToken: string | null = null;
  private accessTokenExpiresAt = 0;
  private refreshInFlight: Promise<boolean> | null = null;
  private sessionExpiredListeners = new Set<() => void>();

  constructor(private readonly baseUrl: string) {}

  setSession(token: TokenResponse | null): void {
    this.accessToken = token?.accessToken ?? null;
    this.accessTokenExpiresAt = token ? Date.parse(token.accessTokenExpiresAt) : 0;
  }

  hasSession(): boolean {
    return this.accessToken !== null;
  }

  onSessionExpired(listener: () => void): () => void {
    this.sessionExpiredListeners.add(listener);
    return () => this.sessionExpiredListeners.delete(listener);
  }

  /** Obtains a new access token from the refresh cookie. Resolves false when the session ended. */
  refresh(): Promise<boolean> {
    this.refreshInFlight ??= (async () => {
      try {
        const res = await fetch(this.url('/auth/refresh'), {
          method: 'POST',
          credentials: 'include',
          headers: {
            accept: 'application/json',
            [CSRF_HEADER]: '1',
            'x-request-id': newRequestId(),
          },
        });
        if (!res.ok) {
          this.setSession(null);
          return false;
        }
        const body = (await res.json()) as { data: TokenResponse };
        this.setSession(body.data);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  get<T>(path: string, options: Omit<RequestOptions, 'body'> = {}): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, { ...options, body: body ?? {} });
  }

  put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body: body ?? {} });
  }

  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body: body ?? {} });
  }

  delete<T = void>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  /**
   * Uploads a file as the raw request body (the API reads metadata from the query string and
   * sniffs the content server-side; the declared type is only a hint).
   */
  upload<T>(
    path: string,
    file: Blob,
    query: Record<string, QueryValue> = {},
    method: 'POST' | 'PUT' = 'POST',
  ): Promise<T> {
    return this.request<T>(method, path, {
      query,
      rawBody: file,
      headers: { 'content-type': file.type || 'application/octet-stream' },
    });
  }

  /** Authenticated binary download (documents, photos). */
  getBlob(
    path: string,
    options: Omit<RequestOptions, 'body' | 'responseType'> = {},
  ): Promise<Blob> {
    return this.request<Blob>('GET', path, { ...options, responseType: 'blob' });
  }

  /** POST to a cookie-authenticated auth endpoint (logout). */
  async postWithCsrf(path: string): Promise<void> {
    await fetch(this.url(path), {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json', [CSRF_HEADER]: '1', 'x-request-id': newRequestId() },
    });
  }

  async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {},
    retried = false,
  ): Promise<T> {
    if (
      !options.anonymous &&
      this.accessToken &&
      Date.now() > this.accessTokenExpiresAt - EXPIRY_SKEW_MS
    ) {
      await this.refresh();
    }

    const requestId = newRequestId();
    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-request-id': requestId,
      ...options.headers,
    };
    if (options.body !== undefined && options.rawBody === undefined) {
      headers['content-type'] = 'application/json';
    }
    if (!options.anonymous && this.accessToken)
      headers.authorization = `Bearer ${this.accessToken}`;

    let response: Response;
    try {
      response = await fetch(this.url(path, options.query), {
        method,
        headers,
        credentials: 'include',
        body:
          options.rawBody !== undefined
            ? options.rawBody
            : options.body !== undefined
              ? JSON.stringify(options.body)
              : undefined,
        signal: options.signal,
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') throw error;
      throw new ApiError(
        0,
        'NETWORK_ERROR',
        'Unable to reach the server. Check your connection and try again.',
        requestId,
      );
    }

    if (response.status === 204) return undefined as T;
    if (response.ok && options.responseType === 'blob') return (await response.blob()) as T;

    const payload = await response.json().catch(() => null);
    if (response.ok) return payload as T;

    const envelope = (
      payload as {
        error?: { code?: string; message?: string; details?: ApiErrorDetail[]; requestId?: string };
      } | null
    )?.error;
    const error = new ApiError(
      response.status,
      envelope?.code ?? `HTTP_${response.status}`,
      envelope?.message ?? 'An unexpected error occurred.',
      envelope?.requestId ?? response.headers.get('x-request-id') ?? requestId,
      envelope?.details ?? [],
    );

    if (response.status === 401 && !options.anonymous) {
      // An expired/invalid access token is recoverable once via the refresh cookie; any other
      // 401 (session revoked, account disabled) - or a failed refresh - ends the session locally.
      if (AUTH_RETRY_CODES.has(error.code) && !retried && (await this.refresh())) {
        return this.request<T>(method, path, options, true);
      }
      this.setSession(null);
      for (const listener of this.sessionExpiredListeners) listener();
    }
    throw error;
  }

  private url(path: string, query?: Record<string, QueryValue>): string {
    const base = this.baseUrl.replace(/\/$/, '');
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return `${base}${path}${qs ? `?${qs}` : ''}`;
  }
}

export const apiClient = new ApiClient(import.meta.env.VITE_API_BASE_URL || '/api/v1');
